import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

/**
 * Payload for POST /admin/locations. Locations are free, so this carries no
 * payment_intent_id and no payment_type.
 * @typedef {Object} LocationCreatePayload
 * @property {number|string} campus_id
 * @property {string} name - Required, max 120 characters.
 * @property {string} [address] - Optional, max 255 characters.
 * @property {"location"|"equipment"} [type]
 */

/**
 * A Location as returned by the list endpoint. The old subscription fields
 * (price_tier, monthly_amount, credit_applied, subscription_status, ...) are
 * gone from creation responses and must not be read.
 * @typedef {Object} Location
 * @property {number} id
 * @property {number} campus_id
 * @property {string} name
 * @property {string} address
 * @property {"location"|"equipment"} type
 * @property {boolean} is_subscription_based - True only for historical locations.
 * @property {boolean} is_deleted_by_admin
 */

// Initial state for the Location slice
const initialState = {
  // locationData: {},
  locationData: {
    data: [], // Initialize data as an empty array
  },
  status: "idle",
  campusId: "",
  error: null,
};

// Destructure the httpRequest function from Http service
const { httpRequest } = Http();

/**
 * Async thunk for adding a new Location. Locations are free, so this is called
 * directly with no payment intent and no Stripe step.
 * @param {LocationCreatePayload} data - The Location data to be added.
 * @returns {undefined} - The endpoint answers 201 with an empty body.
 */
export const addLocation = createAsyncThunk(
  "createLocation",
  async ({ data, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: data,
          url: "admin/locations",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.createLocation.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for fetching the list of Location.
 * @returns {Array} - List of Location.
 */
export const locationList = createAsyncThunk(
  "listLocation",
  async ({ campusId, pageCount, navigate }, { rejectWithValue }) => {
    try {
      const data = await httpRequest(
        {
          method: "GET",
          url: `admin/locations?campus_id=${campusId}&page=${pageCount}`,
        },
        navigate
      );
      return {
        data,
        campusId,
      };
    } catch (err) {
      return rejectWithValue(err.message || messages.listLocation.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for updating an existing Location.
 * @param {Object} data - The Location data to be updated.
 * @param {string} id - The ID of the Location to be updated.
 * @returns {Object} - The updated Location data.
 */
export const updateLocation = createAsyncThunk(
  "editLocation",
  async ({ data, id, navigate }, { rejectWithValue }) => {
    try {
      await httpRequest(
        {
          method: "PUT",
          body: data,
          url: `admin/locations/${id}`,
        },
        navigate
      );
      return { data, id };
    } catch (err) {
      return rejectWithValue(err.message || messages.editLocation.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for fetching a single Location by ID.
 * Note: Currently not in use.
 * @param {string} id - The ID of the Location to be fetched.
 * @returns {Object} - The fetched Location data.
 */
export const singleLocation = createAsyncThunk(
  "singleLocation",
  async ({ id, campusId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/locations/${id}?campus_id=${campusId}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.singleLocation.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for deleting a Location by ID.
 * @param {string} id - The ID of the Location to be deleted.
 * @returns {Object} - The response from the delete request.
 */
export const deleteLocation = createAsyncThunk(
  "deleteLocation",
  async ({ id, campusId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "DELETE",
          url: `admin/locations/${id}?campus_id=${campusId}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.deleteLocation.error, {
        cause: err.cause,
      });
    }
  }
);

// Create a slice for Location-related actions and state management
const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    /**
     * Reducer for showing toast messages.
     * @param {Object} state - The current state.
     * @param {Object} action - The action containing payload data.
     */
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, locationState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, locationState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            addLocation.pending,
            updateLocation.pending,
            locationList.pending,
            deleteLocation.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          action.meta.arg.setLoader(true);
        }
      )
      .addMatcher(
        (action) =>
          [
            addLocation.fulfilled,
            updateLocation.fulfilled,
            locationList.fulfilled,
            deleteLocation.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";

          if (action.type === "listLocation/fulfilled") {

            if (state.campusId !== action.payload.campusId) {
              state.locationData = {
                data: [],
              };
            }

            state.campusId = action.payload.campusId;

            // state.locationData = {
            //   ...action.payload.data.locations,
            //   data: [
            //     ...state.locationData.data,
            //     ...action.payload.data.locations.data,
            //   ],
            // };

            const existingData = state.locationData.data;
            const newData = action.payload.data.locations.data;

            const mergedData = [...existingData, ...newData].reduce(
              (acc, current) => {
                if (!acc.find((item) => item.id === current.id)) {
                  acc.push(current);
                }
                return acc;
              },
              []
            );

            state.locationData = {
              ...action.payload.data.locations,
              data: mergedData,
            };
          }

          // if (["listLocation"].includes(action.type.split("/")[0])) {
          //   state.locationData = {
          //     ...action.payload.locations,
          //     data: [...state.locationData.data, ...action.payload.locations.data],
          //   };
          // }

          // Create
          if ("createLocation" === action.type.split("/")[0]) {
            state.locationData = {
              data: [],
            };
          }

          // Update state in case of edit
          // if (action.type === "editLocation/fulfilled") {
          //   const existingPost = state.locationData.data.find(
          //     (loc) => loc.id === action.payload.id
          //   );
          //   if (existingPost) {
          //     existingPost.name = action.payload.data.name;
          //     existingPost.address = action.payload.data.address;
          //   }
          // }

          // Update state in case of edit
          if ("editLocation" === action.type.split("/")[0]) {
            const existingPost = state.locationData.data.find(
              (loc) => loc.id === action.payload.id
            );
            if (existingPost) {
              existingPost.name = action.payload.data.name;
              existingPost.address = action.payload.data.address;
              existingPost.type = action.payload.data.type;
            }
          }

          // Delete
          if ("deleteLocation" === action.type.split("/")[0]) {
            state.locationData = {
              data: [],
            };
          }

          action.meta.arg.setLoader(false);
          // Show toast only in case of add, edit, delete Location
          if (
            [
              "createLocation/fulfilled",
              "editLocation/fulfilled",
              "deleteLocation/fulfilled",
            ].includes(action.type)
          ) {
            const actionKey = action.type.split("/")[0];
            locationSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message || messages[actionKey].success,
              },
            });
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            addLocation.rejected,
            updateLocation.rejected,
            locationList.rejected,
            deleteLocation.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          state.error =
            action.payload || messages[action.type.split("/")[0]].error;
          action.meta.arg.setLoader(false);
          locationSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

export default locationSlice.reducer;
